import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { RefreshCw } from "lucide-react";
import VariazioneBadges from "@/components/VariazioneBadges";

const STATO_BADGE = {
  pending:   { bg: "#1565c0", color: "white", txt: "● PEND" },
  confirmed: { bg: "#1b5e20", color: "white", txt: "✓ APPR" },
  waiting:   { bg: "#ef6c00", color: "white", txt: "⏳ ATT" },
  rejected:  { bg: "#424242", color: "white", txt: "✗ RIF" },
  recesso:   { bg: "#c62828", color: "white", txt: "⊘ REC" },
  pagata_da_verificare: { bg: "#0d47a1", color: "white", txt: "💳 VER" },
  pagata:    { bg: "#2e7d32", color: "white", txt: "💳 OK" },
  in_attesa_pagamento: { bg: "#7b1fa2", color: "white", txt: "💳 LNK" },
  accettata_mail_inviata: { bg: "#7b1fa2", color: "white", txt: "💳 LNK" },
  confermata_genitore: { bg: "#00695c", color: "white", txt: "✓✓ CNF" },
  scaduta:   { bg: "#5d4037", color: "white", txt: "⏰ SCD" },
  chiusa:    { bg: "#37474f", color: "white", txt: "🔒 CHIU" },
};

const fmtAnnoScol = (a) => {
  const n = Number(a);
  if (!n) return "—";
  return `${n}/${String(n + 1).slice(-2)}`;
};
const fmtDateShort = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return "—"; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

export default function ExtraAlunniView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [filterSettore, setFilterSettore] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [filterAnno, setFilterAnno] = useState("");
  const [openCod, setOpenCod] = useState(null);
  const [sortMode, setSortMode] = useState("alpha"); // alpha | iscr | ultima

  const carica = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/mensa-matrice/extra-alunni");
      setItems(r.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento elenco alunni Extra");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carica(); }, []);

  // Quando l'utente filtra per anno, sostituiamo i valori "cross-year" con quelli
  // dell'anno selezionato (totale iscrizioni, stati, ultima iscrizione, ultimo
  // corso, corsi distinti). Se l'anno non è selezionato, restano i valori globali.
  const applyYearOverride = (it) => {
    if (!filterAnno) return it;
    const stats = (it.stats_by_anno || {})[String(filterAnno)];
    if (!stats) return it;
    return {
      ...it,
      totale_iscrizioni: stats.totale ?? 0,
      stati_count: stats.stati_count || {},
      corsi_distinti: stats.corsi_distinti ?? 0,
      corsi_anno: stats.corsi || [],
      ultima_iscrizione_at: stats.ultima_iscrizione_at || null,
      ultimo_corso: stats.ultimo_corso || null,
      ultimo_corso_descr: stats.ultimo_corso_descr || null,
      ultima_anno: Number(filterAnno),
      anni: [Number(filterAnno)],
      has_contabile_count: stats.has_contabile_count ?? 0,
    };
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let out = items.filter((it) => {
      if (filterSettore && (it.settore || "") !== filterSettore) return false;
      if (filterAnno && !(it.anni || []).map(String).includes(String(filterAnno))) return false;
      if (filterStato) {
        // Quando c'è un filtro anno, lo stato va valutato sull'anno selezionato
        const sc = filterAnno
          ? (((it.stats_by_anno || {})[String(filterAnno)] || {}).stati_count || {})
          : (it.stati_count || {});
        if (!sc[filterStato]) return false;
      }
      if (s) {
        const blob = `${it.cognome} ${it.nome} ${it.cod_fis} ${it.classe} ${it.alunno_cod}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    }).map(applyYearOverride);
    if (sortMode === "alpha") {
      out = [...out].sort((a, b) => (`${a.cognome}${a.nome}`).localeCompare(`${b.cognome}${b.nome}`));
    } else if (sortMode === "iscr") {
      out = [...out].sort((a, b) => (b.totale_iscrizioni || 0) - (a.totale_iscrizioni || 0));
    } else if (sortMode === "ultima") {
      out = [...out].sort((a, b) => (b.ultima_iscrizione_at || "").localeCompare(a.ultima_iscrizione_at || ""));
    }
    return out;
  }, [items, search, filterSettore, filterStato, filterAnno, sortMode]);

  const anniPresenti = useMemo(() => {
    const s = new Set();
    items.forEach((it) => (it.anni || []).forEach((a) => a != null && s.add(a)));
    return [...s].sort((a, b) => Number(b) - Number(a));
  }, [items]);

  const settoriPresenti = useMemo(() => {
    const s = new Set();
    items.forEach((it) => { if (it.settore) s.add(it.settore); });
    return Array.from(s).sort();
  }, [items]);

  const totIscrizioni = filtered.reduce((s, it) => s + (it.totale_iscrizioni || 0), 0);

  return (
    <div style={{ padding: 16 }} data-testid="extra-alunni-view">
      <div style={{ background: "linear-gradient(90deg,#0d47a1,#1565c0)", color: "white", padding: "12px 18px", marginBottom: 12, fontSize: 14, fontWeight: 800, letterSpacing: 0.5, borderRadius: 4 }}>
        👥 ALUNNI ISCRITTI AD ATTIVITÀ EXTRA — Lista dinamica cross-year
        <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 12, opacity: 0.9 }}>
          Aggiornata automaticamente: ogni nuovo alunno iscritto a un'attività appare qui.
        </span>
      </div>

      {/* Toolbar filtri */}
      <div style={{ background: "#f5f5f5", padding: 10, marginBottom: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderRadius: 4 }}>
        <select value={filterAnno} onChange={(e) => setFilterAnno(e.target.value)}
                data-testid="extra-alunni-anno"
                style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #1565c0", color: "#0d47a1", fontWeight: 700, borderRadius: 3 }}>
          <option value="">Tutti gli anni</option>
          {anniPresenti.map((a) => <option key={a} value={a}>{fmtAnnoScol(a)}</option>)}
        </select>
        <input
          type="text"
          placeholder="🔍 Cerca cognome, nome, CF, classe o cod..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="extra-alunni-search"
          style={{ flex: "1 1 280px", padding: "6px 10px", border: "1px solid #bbb", fontSize: 12, borderRadius: 3 }}
        />
        <select value={filterSettore} onChange={(e) => setFilterSettore(e.target.value)}
                data-testid="extra-alunni-settore"
                style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 }}>
          <option value="">Tutti i settori</option>
          {settoriPresenti.map((s) => <option key={s} value={s}>Settore {s}</option>)}
        </select>
        <select value={filterStato} onChange={(e) => setFilterStato(e.target.value)}
                data-testid="extra-alunni-stato"
                style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 }}>
          <option value="">Tutti gli stati</option>
          <option value="pending">Con PENDING</option>
          <option value="confirmed">Con APPROVATE</option>
          <option value="waiting">Con LISTA ATTESA</option>
          <option value="pagata_da_verificare">Con DA VERIFICARE</option>
          <option value="pagata">Con PAGATE OK</option>
          <option value="rejected">Con RIFIUTATE</option>
        </select>
        <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}
                data-testid="extra-alunni-sort"
                style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 }}>
          <option value="alpha">Ordina Cognome ↑</option>
          <option value="iscr">Ordina N° iscrizioni ↓</option>
          <option value="ultima">Ordina Ultima iscriz. ↓</option>
        </select>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "#555" }} data-testid="extra-alunni-counter">
          <b>{filtered.length}</b> alunn{filtered.length === 1 ? "o" : "i"} · <b>{totIscrizioni}</b> iscrizioni totali
        </div>
        <button onClick={carica} disabled={loading}
                data-testid="extra-alunni-refresh"
                style={{ padding: "6px 12px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          {loading ? "…" : "🔄"}
        </button>
      </div>

      {err && (
        <div style={{ background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, marginBottom: 8, fontSize: 12, borderRadius: 3 }}>
          {err}
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", background: "white" }}>
          <thead>
            <tr style={{ background: "#1a237e", color: "white" }}>
              <th style={{ textAlign: "center", padding: "6px 4px", width: 34 }} title="Posizione nell'elenco filtrato">#</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>COGNOME NOME</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>COD FISCALE</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>SETT.</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>CLASSE</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>CONTATTI</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>N° ISC.</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>ANNI</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>STATI</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>ULTIMA ISCRIZIONE</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} style={{ textAlign: "center", padding: 20, color: "#888" }}>Caricamento…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={11} style={{ textAlign: "center", padding: 20, color: "#888", fontStyle: "italic" }}>
                Nessun alunno trovato con i filtri impostati.
              </td></tr>
            )}
            {!loading && filtered.map((it, i) => {
              const stati = it.stati_count || {};
              return (
                <tr key={it.alunno_cod} data-testid={`extra-alunno-row-${it.alunno_cod}`}
                    style={{ borderTop: "1px solid #eee", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                  <td data-testid={`extra-alunno-pos-${it.alunno_cod}`}
                      style={{ padding: "6px 4px", textAlign: "center", fontFamily: "Consolas, monospace",
                               fontWeight: 800, color: "#546e7a", background: "#eceff1" }}>{i + 1}</td>
                  <td style={{ padding: "6px 8px", fontWeight: 700 }}>
                    {it.cognome} {it.nome}
                    <div style={{ fontSize: 9, color: "#888", fontFamily: "monospace" }}>cod: {it.alunno_cod}</div>
                  </td>
                  <td style={{ padding: "6px 8px", fontFamily: "monospace", fontSize: 10 }}>{it.cod_fis || "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 800, color: it.settore ? "#0d47a1" : "#ccc" }}>{it.settore || "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 600 }}>{it.classe || "—"}</td>
                  <td style={{ padding: "6px 8px", fontSize: 10 }}>
                    {it.telefono && <div style={{ fontFamily: "monospace" }}>📞 {it.telefono}</div>}
                    {it.email && <div title={it.email} style={{ color: "#555" }}>✉ {it.email}</div>}
                    {!it.telefono && !it.email && <span style={{ color: "#ccc" }}>—</span>}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "center", fontWeight: 800, fontSize: 13, color: "#ef6c00" }}>{it.totale_iscrizioni}</td>
                  <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: "monospace" }}>
                    {(() => {
                      // Sparkline anno-per-anno: usa sempre stats_by_anno per mostrare il
                      // breakdown cross-year (numero iscrizioni per anno), indipendentemente
                      // dall'eventuale filtro anno applicato sulla riga.
                      const sb = it.stats_by_anno || {};
                      const yrs = Object.keys(sb).sort((a, b) => Number(b) - Number(a));
                      if (yrs.length === 0) {
                        // Fallback se backend non ha valorizzato stats_by_anno
                        return (it.anni || []).map((a) => (
                          <span key={a} style={{ display: "inline-block", padding: "1px 5px", margin: 1, background: "#e8eaf6", color: "#1a237e", fontWeight: 700, borderRadius: 2 }}>{fmtAnnoScol(a)}</span>
                        ));
                      }
                      return yrs.map((y) => {
                        const tot = sb[y]?.totale || 0;
                        const isActive = filterAnno && String(filterAnno) === String(y);
                        return (
                          <span key={y}
                            title={`Anno ${fmtAnnoScol(y)}: ${tot} iscrizion${tot === 1 ? "e" : "i"}`}
                            data-testid={`extra-alunno-anno-${it.alunno_cod}-${y}`}
                            style={{
                              display: "inline-block",
                              padding: "1px 5px",
                              margin: 1,
                              background: isActive ? "#1a237e" : "#e8eaf6",
                              color: isActive ? "white" : "#1a237e",
                              fontWeight: 700,
                              borderRadius: 2,
                              border: isActive ? "1px solid #1a237e" : "1px solid transparent",
                            }}>
                            {fmtAnnoScol(y)}<span style={{ marginLeft: 3, fontSize: 9, opacity: 0.85 }}>×{tot}</span>
                          </span>
                        );
                      });
                    })()}
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    {Object.entries(stati).map(([k, v]) => {
                      const b = STATO_BADGE[k] || STATO_BADGE.pending;
                      return (
                        <span key={k} title={`${k}: ${v}`}
                              style={{ display: "inline-block", padding: "1px 5px", margin: 1, background: b.bg, color: b.color, fontSize: 9, fontWeight: 700, borderRadius: 2 }}>
                          {b.txt} {v > 1 ? `×${v}` : ""}
                        </span>
                      );
                    })}
                  </td>
                  <td style={{ padding: "6px 8px", fontSize: 10 }}>
                    {filterAnno && (it.corsi_anno || []).length > 0 ? (
                      <>
                        <div style={{ fontWeight: 700, color: "#0d47a1" }} data-testid={`extra-alunno-corsi-${it.alunno_cod}`}>
                          {(it.corsi_anno || []).map((c, ci) => (
                            <span key={ci} title={c.codice}
                              style={{ display: "inline-block", padding: "1px 6px", margin: "1px 2px 1px 0", background: "#e3f2fd", color: "#0d47a1", borderRadius: 2, fontSize: 10 }}>
                              {c.descrizione || c.codice}
                            </span>
                          ))}
                        </div>
                        <div style={{ color: "#888", fontFamily: "monospace", marginTop: 2 }}>
                          ultima: {fmtDateTime(it.ultima_iscrizione_at)} · {fmtAnnoScol(it.ultima_anno)}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontWeight: 700 }}>{it.ultimo_corso_descr || it.ultimo_corso || "—"}</div>
                        <div style={{ color: "#888", fontFamily: "monospace" }}>{fmtDateTime(it.ultima_iscrizione_at)} · {fmtAnnoScol(it.ultima_anno)}</div>
                      </>
                    )}
                    {(it.variazioni || [])
                      .filter((v) => !filterAnno || String(v.anno) === String(filterAnno))
                      .map((v, vi) => (
                        <div key={vi} data-testid={`extra-alunno-nota-var-${it.alunno_cod}-${vi}`}
                          style={{ marginTop: 3, fontSize: 9, color: "#6a1b9a", background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 3, padding: "2px 5px", lineHeight: 1.3 }}>
                          <b>VARIATO</b>{v.data ? ` ${fmtDateShort(v.data)}` : ""}{v.corso ? ` · ${v.corso}` : ""}
                          {v.note ? <div style={{ fontStyle: "italic", color: "#455a64", fontWeight: 400 }}>{v.note}</div> : null}
                        </div>
                      ))}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "center" }}>
                    <button onClick={() => setOpenCod(it.alunno_cod)} data-testid={`extra-alunno-log-${it.alunno_cod}`}
                            title="Vedi log iscrizioni e modifica"
                            style={{ padding: "4px 10px", background: "#6a1b9a", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                      📜 Log
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openCod !== null && (
        <ExtraAlunnoLogModal alunnoCod={openCod} onClose={() => setOpenCod(null)} onChanged={carica} />
      )}
    </div>
  );
}

function ExtraAlunnoLogModal({ alunnoCod, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // iscrizione in modifica
  const [confirmDel, setConfirmDel] = useState(null);
  const [cambioItem, setCambioItem] = useState(null);

  const carica = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get(`/mensa-matrice/extra-alunni/${alunnoCod}/iscrizioni`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carica(); }, [alunnoCod]);

  const setStato = async (iid, newStato) => {
    try {
      await api.put(`/mensa-matrice/extra-iscrizioni/${iid}`, { stato_iscrizione: newStato });
      await carica();
      onChanged && onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore aggiornamento stato");
    }
  };

  const elimina = async (iid, descr) => {
    if (!window.confirm(`Eliminare definitivamente l'iscrizione a "${descr}"?`)) return;
    try {
      await api.delete(`/mensa-matrice/extra-iscrizioni/${iid}`);
      await carica();
      onChanged && onChanged();
      setConfirmDel(null);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="extra-alunno-log-modal"
           style={{ background: "white", width: "100%", maxWidth: 1080, maxHeight: "92vh", overflow: "auto", borderRadius: 6 }}>
        <div style={{ background: "linear-gradient(90deg,#4a148c,#6a1b9a)", color: "white", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 5 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>📜 Log iscrizioni Extra · {data?.alunno?.cognome} {data?.alunno?.nome}</div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>
              Cod: {alunnoCod}
              {data?.alunno?.cod_fis && <> · CF: <span style={{ fontFamily: "monospace" }}>{data.alunno.cod_fis}</span></>}
              {data?.alunno?.email && <> · ✉ {data.alunno.email}</>}
              {data?.alunno?.telefono && <> · 📞 {data.alunno.telefono}</>}
              <> · Totale iscrizioni: <b>{data?.totale ?? "…"}</b></>
            </div>
          </div>
          <button onClick={onClose} data-testid="extra-alunno-log-close"
                  style={{ background: "none", border: "none", color: "white", fontSize: 24, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 16 }}>
          {loading && <div style={{ textAlign: "center", padding: 20, color: "#888" }}>Caricamento…</div>}
          {err && <div style={{ background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, fontSize: 12 }}>{err}</div>}

          {!loading && data && (data.gruppi || []).length === 0 && (
            <div style={{ textAlign: "center", padding: 30, color: "#888", fontStyle: "italic" }}>
              Nessuna iscrizione registrata.
            </div>
          )}

          {!loading && data && (data.gruppi || []).map((g) => (
            <div key={g.anno} style={{ marginBottom: 18 }}>
              <div style={{ background: "#1565c0", color: "white", padding: "8px 12px", fontSize: 13, fontWeight: 800, letterSpacing: 0.5 }}>
                ANNO {fmtAnnoScol(g.anno)} · {g.items.length} iscrizion{g.items.length === 1 ? "e" : "i"}
              </div>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", border: "1px solid #ddd" }}>
                <thead>
                  <tr style={{ background: "#37474f", color: "white" }}>
                    <th style={{ textAlign: "left", padding: 6 }}>COD</th>
                    <th style={{ textAlign: "left", padding: 6 }}>CORSO</th>
                    <th style={{ textAlign: "center", padding: 6 }}>TARIFFA</th>
                    <th style={{ textAlign: "center", padding: 6 }}>GIORNI</th>
                    <th style={{ textAlign: "center", padding: 6 }}>STATO</th>
                    <th style={{ textAlign: "center", padding: 6 }}>CONTABILE</th>
                    <th style={{ textAlign: "left", padding: 6 }}>RECESSO / NOTE</th>
                    <th style={{ textAlign: "left", padding: 6 }}>CREATA</th>
                    <th style={{ textAlign: "center", padding: 6, width: 220 }}>AZIONI</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((it) => {
                    const stato = it.stato_iscrizione || (it.in_lista_attesa ? "waiting" : "pending");
                    const b = STATO_BADGE[stato] || STATO_BADGE.pending;
                    const isEdit = editing && editing._id === it._id;
                    return (
                      <tr key={it._id} data-testid={`extra-alunno-log-row-${it._id}`} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: 6, fontFamily: "monospace", color: "#0d47a1", fontWeight: 700 }}>{it.corso_codice}</td>
                        <td style={{ padding: 6 }}>
                          {it.corso_descrizione}
                          {it.usa_mensa === false && (
                            <div title="Alunno ha NEGATO l'iscrizione automatica alla mensa"
                                 style={{ display: "inline-block", marginLeft: 6, padding: "1px 6px",
                                          background: "#c62828", color: "white", fontSize: 9, fontWeight: 800,
                                          borderRadius: 2, verticalAlign: "middle" }}>
                              🚫 NO MENSA
                            </div>
                          )}
                        </td>
                        <td style={{ padding: 6, textAlign: "center" }}>
                          <div>{it.tariffa_etichetta || "—"}</div>
                          {it.tariffa_importo != null && <div style={{ fontSize: 10, fontWeight: 700, color: "#1b5e20" }}>€ {Number(it.tariffa_importo).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>}
                        </td>
                        <td style={{ padding: 6, textAlign: "center", fontFamily: "monospace" }}>
                          <GiorniBadges value={it.giorni_scelti} nrGiorni={it.nr_giorni} giorniNum={it.giorni} corsoAmmessi={it.corso_giorni_ammessi} />
                        </td>
                        <td style={{ padding: 6, textAlign: "center" }}>
                          <span style={{ padding: "2px 6px", background: b.bg, color: b.color, fontWeight: 800, fontSize: 10, borderRadius: 2 }}>{b.txt}</span>
                        </td>
                        <td style={{ padding: 6, textAlign: "center" }}>
                          {it.has_contabile ? (
                            <span title={it.contabile_filename || ""} style={{ color: "#1b5e20", fontWeight: 700 }}>✓</span>
                          ) : (
                            <span style={{ color: "#ccc" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: 6, fontSize: 10 }}>
                          {it.data_recesso && !it.variata ? <div style={{ color: "#c62828", fontWeight: 700 }}>RECESSO: {it.data_recesso}</div> : null}
                          <VariazioneBadges it={it} />
                          {it.note_admin ? <div style={{ color: "#555", marginTop: 2 }}>{it.note_admin}</div> : null}
                          {it.note_studente ? <div style={{ color: "#666", fontStyle: "italic", marginTop: 2 }}>"{it.note_studente}"</div> : null}
                        </td>
                        <td style={{ padding: 6, fontSize: 10, color: "#666", fontFamily: "monospace" }}>{fmtDateTime(it.created_at)}</td>
                        <td style={{ padding: 6, textAlign: "center" }}>
                          {!isEdit && (
                            <div style={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
                              {stato !== "confirmed" && (
                                <button onClick={() => setStato(it._id, "confirmed")} data-testid={`extra-alunno-approve-${it._id}`}
                                        title="Approva" style={btnSm("#2e7d32")}>✓</button>
                              )}
                              {stato !== "waiting" && (
                                <button onClick={() => setStato(it._id, "waiting")} title="Lista attesa" style={btnSm("#ef6c00")}>⏳</button>
                              )}
                              {stato !== "rejected" && (
                                <button onClick={() => setStato(it._id, "rejected")} title="Rifiuta" style={btnSm("#424242")}>✗</button>
                              )}
                              <button onClick={() => setEditing({ ...it })} data-testid={`extra-alunno-edit-${it._id}`}
                                      title="Modifica note/recesso" style={btnSm("#1565c0")}>✏</button>
                              <button onClick={() => setCambioItem({ ...it })} data-testid={`extra-alunno-cambio-${it._id}`}
                                      title="Cambio corso (variazione)"
                                      style={{ background: "#6a1b9a", color: "white", border: "none", padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                                <RefreshCw size={14} strokeWidth={2.5} /> Cambio
                              </button>
                              <button onClick={() => elimina(it._id, it.corso_descrizione)} data-testid={`extra-alunno-del-${it._id}`}
                                      title="Elimina iscrizione" style={btnSm("#c62828")}>🗑</button>
                            </div>
                          )}
                          {isEdit && (
                            <div style={{ display: "flex", gap: 3, justifyContent: "center" }}>
                              <button onClick={() => setEditing(null)} style={btnSm("#888")}>Annulla</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <InlineEditModal
          isc={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await carica(); onChanged && onChanged(); }}
        />
      )}
      {cambioItem && (
        <CambioCorsoModal
          isc={cambioItem}
          onClose={() => setCambioItem(null)}
          onSaved={async () => { setCambioItem(null); await carica(); onChanged && onChanged(); }}
        />
      )}
      {confirmDel && (<></>)}
    </div>
  );
}

function btnSm(bg) {
  return {
    background: bg,
    color: "white",
    border: "none",
    padding: "3px 8px",
    fontSize: 10,
    fontWeight: 700,
    cursor: "pointer",
    borderRadius: 2,
  };
}

const GIORNI_SETT = ["LUN", "MAR", "MER", "GIO", "VEN"];
// 0=LUN, 1=MAR, 2=MER, 3=GIO, 4=VEN (convention Python weekday())
const NUM_TO_GIORNO = { 0: "LUN", 1: "MAR", 2: "MER", 3: "GIO", 4: "VEN" };

function GiorniBadges({ value, nrGiorni, giorniNum, corsoAmmessi }) {
  // Priorità:
  //   1. giorni_scelti (stringa "LUN,MAR" o array)
  //   2. giorni (array numerico [1,3] sull'iscrizione stessa)
  //   3. corso_giorni_ammessi (array numerico dal corso)
  let arr = [];
  let fromCorso = false;
  if (Array.isArray(value)) arr = value;
  else if (typeof value === "string" && value.trim()) {
    arr = value.split(/[,;\s]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
  }
  if (arr.length === 0 && Array.isArray(giorniNum) && giorniNum.length > 0) {
    arr = giorniNum.map(n => NUM_TO_GIORNO[Number(n)]).filter(Boolean);
  }
  if (arr.length === 0 && Array.isArray(corsoAmmessi) && corsoAmmessi.length > 0) {
    arr = corsoAmmessi.map(n => NUM_TO_GIORNO[Number(n)]).filter(Boolean);
    fromCorso = true;
  }
  const set = new Set(arr);
  if (set.size === 0 && !nrGiorni) {
    return <span style={{ color: "#ccc" }}>—</span>;
  }
  // Colori diversi: verde se scelti dallo studente, blu se ereditati dal corso
  const onBg = fromCorso ? "#1565c0" : "#1b5e20";
  const onBorder = fromCorso ? "#1565c0" : "#1b5e20";
  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
      <div style={{ display: "inline-flex", gap: 2 }}>
        {GIORNI_SETT.map(g => {
          const on = set.has(g);
          return (
            <span key={g}
                  title={on ? (fromCorso ? `${g} (giorno del corso)` : `${g} selezionato`) : g}
                  style={{
                    display: "inline-block",
                    minWidth: 22,
                    padding: "2px 4px",
                    fontSize: 9,
                    fontWeight: 800,
                    textAlign: "center",
                    background: on ? onBg : "#eceff1",
                    color: on ? "white" : "#b0bec5",
                    border: on ? `1px solid ${onBorder}` : "1px solid #cfd8dc",
                    borderRadius: 2,
                  }}>
              {g}
            </span>
          );
        })}
      </div>
      {fromCorso && (
        <span style={{ fontSize: 8, color: "#1565c0", fontWeight: 600, fontStyle: "italic" }}>
          (giorni del corso)
        </span>
      )}
      {set.size === 0 && nrGiorni ? (
        <span style={{ fontSize: 9, color: "#666" }}>{nrGiorni}gg</span>
      ) : null}
    </div>
  );
}

function InlineEditModal({ isc, onClose, onSaved }) {
  const [stato, setStato] = useState(isc.stato_iscrizione || "pending");
  const [recesso, setRecesso] = useState(isc.data_recesso || "");
  const [noteAdmin, setNoteAdmin] = useState(isc.note_admin || "");
  const [tariffaEt, setTariffaEt] = useState(isc.tariffa_etichetta || "");
  const [tariffaImp, setTariffaImp] = useState(isc.tariffa_importo != null ? String(isc.tariffa_importo) : "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const salva = async () => {
    setSaving(true);
    setErr(null);
    try {
      const body = {
        stato_iscrizione: stato,
        data_recesso: recesso || "",
        note_admin: noteAdmin,
      };
      if (tariffaEt) body.tariffa_etichetta = tariffaEt;
      if (tariffaImp !== "") body.tariffa_importo = Number(tariffaImp);
      await api.put(`/mensa-matrice/extra-iscrizioni/${isc._id}`, body);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="extra-alunno-edit-modal"
           style={{ background: "white", width: "100%", maxWidth: 540, borderRadius: 6 }}>
        <div style={{ background: "#1565c0", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>✏ Modifica iscrizione · {isc.corso_codice}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 10 }}>
          <div style={{ fontSize: 12, color: "#555" }}>{isc.corso_descrizione}</div>

          <label style={lbl}>STATO ISCRIZIONE</label>
          <select value={stato} onChange={(e) => setStato(e.target.value)} data-testid="alunno-edit-stato" style={inp}>
            <option value="pending">● Pending</option>
            <option value="confirmed">✓ Approvata</option>
            <option value="waiting">⏳ Lista attesa</option>
            <option value="rejected">✗ Rifiutata</option>
            <option value="pagata_da_verificare">💳 Pagata da verificare</option>
            <option value="pagata">💳 Pagata</option>
          </select>

          <label style={lbl}>DATA RECESSO (lascia vuoto se attiva)</label>
          <input type="date" value={recesso} onChange={(e) => setRecesso(e.target.value)} data-testid="alunno-edit-recesso" style={inp} />

          <label style={lbl}>TARIFFA — Etichetta</label>
          <input value={tariffaEt} onChange={(e) => setTariffaEt(e.target.value)} placeholder="es. 2 giorni/sett." style={inp} />

          <label style={lbl}>TARIFFA — Importo €</label>
          <input value={tariffaImp} onChange={(e) => setTariffaImp(e.target.value)} type="number" step="0.01" style={inp} />

          <label style={lbl}>NOTE ADMIN</label>
          <textarea value={noteAdmin} onChange={(e) => setNoteAdmin(e.target.value)} rows={3} data-testid="alunno-edit-note" style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />

          {err && <div style={{ background: "#ffebee", color: "#b71c1c", padding: 8, fontSize: 11, border: "1px solid #ef9a9a", borderRadius: 3 }}>{err}</div>}
        </div>
        <div style={{ borderTop: "1px solid #eee", padding: "10px 16px", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>Annulla</button>
          <button onClick={salva} disabled={saving} data-testid="alunno-edit-save"
                  style={{ padding: "6px 16px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 2 }}>
            {saving ? "Salvataggio…" : "💾 Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DayToggle({ selected, onToggle }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {GIORNI_SETT.map((g, i) => {
        const on = selected.has(i);
        return (
          <button key={i} type="button" onClick={() => onToggle(i)}
            data-testid={`cambio-day-${i}`}
            style={{ minWidth: 40, padding: "5px 6px", fontSize: 11, fontWeight: 800, cursor: "pointer",
                     background: on ? "#2e7d32" : "#eceff1", color: on ? "white" : "#90a4ae",
                     border: on ? "1px solid #2e7d32" : "1px solid #cfd8dc", borderRadius: 3 }}>
            {g}
          </button>
        );
      })}
    </div>
  );
}

function CambioCorsoModal({ isc, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [dataCambio, setDataCambio] = useState(isc.data_recesso || today);
  const [giorniNuovi, setGiorniNuovi] = useState(new Set((isc.giorni || []).map(Number)));
  const [note, setNote] = useState("");
  const [nuove, setNuove] = useState([]);           // [{corso_codice, giorni:Set, tariffa_etichetta, importo, credito}]
  const [corsi, setCorsi] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa-matrice/corsi", { params: { anno: isc.anno } });
        setCorsi(r.data?.items || r.data?.corsi || []);
      } catch { setCorsi([]); }
    })();
  }, [isc.anno]);

  const toggleGiorno = (i) => {
    const s = new Set(giorniNuovi);
    s.has(i) ? s.delete(i) : s.add(i);
    setGiorniNuovi(s);
  };
  const addNuova = () => setNuove([...nuove, { corso_codice: "", giorni: new Set(), tariffa_etichetta: "", importo: "", credito: "" }]);
  const updNuova = (idx, patch) => setNuove(nuove.map((n, i) => i === idx ? { ...n, ...patch } : n));
  const delNuova = (idx) => setNuove(nuove.filter((_, i) => i !== idx));
  const toggleNuovaGiorno = (idx, g) => {
    const n = nuove[idx]; const s = new Set(n.giorni);
    s.has(g) ? s.delete(g) : s.add(g);
    updNuova(idx, { giorni: s });
  };

  const salva = async () => {
    setSaving(true); setErr(null);
    try {
      const body = {
        data_cambio: dataCambio || "",
        giorni_nuovi: Array.from(giorniNuovi).sort((a, b) => a - b),
        note,
        nuove_iscrizioni: nuove.filter(n => n.corso_codice).map(n => ({
          corso_codice: n.corso_codice,
          giorni: Array.from(n.giorni).sort((a, b) => a - b),
          tariffa_etichetta: n.tariffa_etichetta || "",
          importo: Number(n.importo || 0),
          credito: Number(n.credito || 0),
        })),
      };
      await api.post(`/mensa-matrice/extra-alunni/iscrizione/${isc._id}/cambio-corso`, body);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio cambio corso");
    } finally { setSaving(false); }
  };

  const giorniOrig = (isc.giorni || []).map(n => NUM_TO_GIORNO[Number(n)]).join(", ") || "—";

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 950, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="cambio-corso-modal"
           style={{ background: "white", width: "100%", maxWidth: 680, maxHeight: "92vh", overflow: "auto", borderRadius: 6 }}>
        <div style={{ background: "linear-gradient(90deg,#4a148c,#6a1b9a)", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}><RefreshCw size={16} strokeWidth={2.5} /> Cambio corso · {isc.corso_codice}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div style={{ background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 4, padding: 10, fontSize: 12 }}>
            <b>{isc.corso_descrizione}</b> — importo attuale {isc.tariffa_importo != null ? `€ ${Number(isc.tariffa_importo).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"} · giorni attuali: <b>{giorniOrig}</b>
            <div style={{ color: "#6a1b9a", marginTop: 4 }}>L'iscrizione resta <b>pagata</b> con l'importo attuale (nessun rimborso). Qui aggiorni i giorni e, se serve, aggiungi nuovi corsi.</div>
          </div>

          <label style={lbl}>DATA CAMBIO (la mensa userà i nuovi giorni da questa data)</label>
          <input type="date" value={dataCambio} onChange={(e) => setDataCambio(e.target.value)} data-testid="cambio-data" style={inp} />

          <label style={lbl}>NUOVI GIORNI del corso attuale (es. tieni solo GIO)</label>
          <DayToggle selected={giorniNuovi} onToggle={toggleGiorno} />

          <label style={lbl}>NOTA VARIAZIONE (es. "da 2gg MAR+GIO a 1gg GIO")</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="cambio-note" style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />

          <div style={{ borderTop: "1px dashed #ccc", paddingTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label style={lbl}>NUOVE ISCRIZIONI (da pagare · nessun addebito automatico)</label>
              <button type="button" onClick={addNuova} data-testid="cambio-add-corso"
                      style={{ padding: "4px 10px", background: "#00695c", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>+ Aggiungi corso</button>
            </div>
            {nuove.map((n, idx) => (
              <div key={idx} data-testid={`cambio-nuova-${idx}`} style={{ border: "1px solid #b2dfdb", background: "#e0f2f1", borderRadius: 4, padding: 8, marginBottom: 8, display: "grid", gap: 6 }}>
                <select value={n.corso_codice} onChange={(e) => {
                  const c = corsi.find(x => x.codice === e.target.value);
                  updNuova(idx, { corso_codice: e.target.value, tariffa_etichetta: n.tariffa_etichetta });
                  void c;
                }} data-testid={`cambio-nuova-corso-${idx}`} style={inp}>
                  <option value="">— Seleziona corso —</option>
                  {corsi.map(c => <option key={c.codice} value={c.codice}>{c.codice} · {c.descrizione}</option>)}
                </select>
                <DayToggleRow giorni={n.giorni} onToggle={(g) => toggleNuovaGiorno(idx, g)} />
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={n.tariffa_etichetta} onChange={(e) => updNuova(idx, { tariffa_etichetta: e.target.value })} placeholder="Etichetta tariffa" style={{ ...inp, flex: 2 }} />
                  <input value={n.importo} onChange={(e) => updNuova(idx, { importo: e.target.value })} type="number" step="0.01" placeholder="Importo €" data-testid={`cambio-nuova-importo-${idx}`} style={{ ...inp, flex: 1 }} />
                  <input value={n.credito} onChange={(e) => updNuova(idx, { credito: e.target.value })} type="number" step="0.01" placeholder="Credito da scalare €" style={{ ...inp, flex: 1 }} />
                  <button type="button" onClick={() => delNuova(idx)} style={{ ...btnSm("#c62828"), padding: "0 10px" }}>🗑</button>
                </div>
                {(Number(n.importo || 0) - Number(n.credito || 0)) !== Number(n.importo || 0) && (
                  <div style={{ fontSize: 11, color: "#00695c", fontWeight: 700 }}>Netto da pagare: € {(Number(n.importo || 0) - Number(n.credito || 0)).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                )}
              </div>
            ))}
          </div>

          {err && <div style={{ background: "#ffebee", color: "#b71c1c", padding: 8, fontSize: 11, border: "1px solid #ef9a9a", borderRadius: 3 }}>{err}</div>}
        </div>
        <div style={{ borderTop: "1px solid #eee", padding: "10px 16px", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0, background: "white" }}>
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>Annulla</button>
          <button onClick={salva} disabled={saving} data-testid="cambio-save"
                  style={{ padding: "6px 16px", background: "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 2 }}>
            {saving ? "Salvataggio…" : "💾 Salva cambio corso"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DayToggleRow({ giorni, onToggle }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {GIORNI_SETT.map((g, i) => {
        const on = giorni.has(i);
        return (
          <button key={i} type="button" onClick={() => onToggle(i)}
            style={{ minWidth: 38, padding: "4px 6px", fontSize: 10, fontWeight: 800, cursor: "pointer",
                     background: on ? "#00695c" : "#eceff1", color: on ? "white" : "#90a4ae",
                     border: on ? "1px solid #00695c" : "1px solid #cfd8dc", borderRadius: 3 }}>
            {g}
          </button>
        );
      })}
    </div>
  );
}


const lbl = { fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.3 };
const inp = { width: "100%", padding: "6px 8px", border: "1px solid #bbb", borderRadius: 3, fontSize: 12, boxSizing: "border-box" };
